import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from '@mui/material';

/**
 * ConfirmationDialog - Reusable confirmation dialog component
 *
 * @param {Object} props
 * @param {boolean} props.open - Whether the dialog is open
 * @param {Function} props.onClose - Called when dialog is closed (cancel or backdrop click)
 * @param {Function} props.onConfirm - Called when confirm button is clicked
 * @param {string} [props.title='Confirm Action'] - Dialog title
 * @param {string|React.ReactNode} [props.message='Are you sure you want to proceed?'] - Dialog message
 * @param {string} [props.confirmText='Confirm'] - Confirm button text
 * @param {string} [props.cancelText='Cancel'] - Cancel button text
 * @param {string} [props.confirmColor='error'] - Confirm button color ('error', 'primary', 'warning', etc.)
 * @param {string} [props.maxWidth='sm'] - Dialog max width
 * @param {boolean} [props.fullWidth=true] - Whether dialog should take full width
 *
 * @example
 * const [open, setOpen] = useState(false);
 *
 * <ConfirmationDialog
 *   open={open}
 *   onClose={() => setOpen(false)}
 *   onConfirm={() => handleDelete()}
 *   title="Delete Entry"
 *   message="Are you sure you want to delete this entry? This action cannot be undone."
 *   confirmText="Delete"
 *   confirmColor="error"
 * />
 */
export const ConfirmationDialog = ({
  open,
  onClose,
  onConfirm,
  title = 'Confirm Action',
  message = 'Are you sure you want to proceed?',
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  confirmColor = 'error',
  maxWidth = 'sm',
  fullWidth = true,
}) => {
  const handleConfirm = () => {
    onConfirm();
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth={maxWidth} fullWidth={fullWidth}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {typeof message === 'string' ? (
          <DialogContentText>{message}</DialogContentText>
        ) : (
          message
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit" variant="outlined">
          {cancelText}
        </Button>
        <Button onClick={handleConfirm} color={confirmColor} variant="contained" autoFocus>
          {confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmationDialog;
